import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface AdminEditContextType {
  editMode: boolean;
  toggleEditMode: () => void;
  getContent: (key: string) => string | undefined;
  saveContent: (key: string, value: string) => Promise<void>;
  isLoading: boolean;
}

const AdminEditContext = createContext<AdminEditContextType>({
  editMode: false,
  toggleEditMode: () => {},
  getContent: () => undefined,
  saveContent: async () => {},
  isLoading: false,
});

export const useAdminEdit = () => useContext(AdminEditContext);

export const AdminEditProvider = ({ children }: { children: ReactNode }) => {
  const { isAdmin } = useAuth();
  const [editMode, setEditMode] = useState(false);
  const queryClient = useQueryClient();

  const { data: contentMap, isLoading } = useQuery({
    queryKey: ["site-content"],
    queryFn: async () => {
      const { data } = await supabase
        .from("platform_settings")
        .select("key, value")
        .like("key", "content:%");
      const map: Record<string, string> = {};
      (data || []).forEach((row) => {
        map[row.key] = typeof row.value === "string" ? row.value : (row.value as any)?.text ?? "";
      });
      return map;
    },
    staleTime: 60_000,
  });

  const toggleEditMode = useCallback(() => {
    if (!isAdmin) return;
    setEditMode((prev) => !prev);
  }, [isAdmin]);

  const getContent = useCallback(
    (key: string): string | undefined => {
      return contentMap?.[`content:${key}`];
    },
    [contentMap]
  );

  const saveContent = useCallback(
    async (key: string, value: string) => {
      const fullKey = `content:${key}`;
      const { error } = await supabase
        .from("platform_settings")
        .upsert({ key: fullKey, value: JSON.stringify({ text: value }) as any }, { onConflict: "key" });
      if (error) {
        toast.error("שגיאה בשמירה");
        console.error(error);
        return;
      }
      toast.success("נשמר בהצלחה ✅");
      queryClient.invalidateQueries({ queryKey: ["site-content"] });
    },
    [queryClient]
  );

  return (
    <AdminEditContext.Provider value={{ editMode: isAdmin && editMode, toggleEditMode, getContent, saveContent, isLoading }}>
      {children}
    </AdminEditContext.Provider>
  );
};
